"use client";

import type { KundaliTranslations } from "./KundaliFormCard.i18n";
import { FieldError, FieldWrapper } from "./KundaliFormCard.fields";
import { PhoneInputE164 } from "@/components/PhoneInputE164";

/**
 * Contact fields for the chat-entry / kundali form (India-default, E.164):
 *   - PhoneField   — phone is the strongest identity/delivery signal and is
 *                    required on every shared kundali entry (ADR-0043). India
 *                    stays the default and keeps its 10-digit +91 UX; the
 *                    country picker exists because real diaspora buyers hold
 *                    +971/+1/+60 numbers. Numeric tel keypad, autofocus, and an
 *                    honest transactional save reason.
 *   - EmailField   — always optional; validated only when non-empty.
 * Both persist via the existing captureReadingContact helper after the reading
 * is created (the form is the single decision — these are part of it, not a
 * second CTA, rule 39). The reading always renders regardless (rule 46).
 */

interface PhoneFieldProps {
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  activeField: string | null;
  t: KundaliTranslations;
  required: boolean;
  error?: string;
}

export function PhoneField({
  value,
  onChange,
  onFocus,
  onBlur,
  activeField,
  t,
  required,
  error,
}: PhoneFieldProps) {
  return (
    <FieldWrapper activeField={activeField} fieldName="phone">
      <label
        htmlFor="phone"
        className="block text-[13px] font-semibold mb-1"
        style={{ color: "var(--ish-dark)" }}
      >
        {t.phone}
      </label>
      <PhoneInputE164
        id="phone"
        name="phone"
        required={required}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={t.phonePlaceholder}
        countryLabel={t.phoneCountryLabel}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? "phone-error" : "phone-reason"}
      />
      <p
        id="phone-reason"
        className="mt-1.5 text-[11px]"
        style={{ color: "var(--ish-text-muted)" }}
      >
        {t.phoneReason}
      </p>
      <FieldError id="phone-error" message={error} />
    </FieldWrapper>
  );
}

interface EmailFieldProps {
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  activeField: string | null;
  t: KundaliTranslations;
  error?: string;
}

export function EmailField({
  value,
  onChange,
  onFocus,
  onBlur,
  activeField,
  t,
  error,
}: EmailFieldProps) {
  return (
    <FieldWrapper activeField={activeField} fieldName="email">
      <label
        htmlFor="email"
        className="block text-[13px] font-semibold mb-1"
        style={{ color: "var(--ish-dark)" }}
      >
        {t.email}{" "}
        <span className="font-normal" style={{ color: "var(--ish-text-faint)" }}>
          {t.optional}
        </span>
      </label>
      <input
        id="email"
        name="email"
        type="email"
        inputMode="email"
        autoComplete="email"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={t.emailPlaceholder}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? "email-error" : undefined}
        className="input-sacred w-full rounded-lg px-4 py-3 text-[14px]"
        style={{ color: "var(--ish-dark)" }}
      />
      <FieldError id="email-error" message={error} />
    </FieldWrapper>
  );
}
